import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "i18next";
import isEmpty from "lodash/isEmpty";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import { EventType } from "@enums/component.enum";
import { slotType } from "@enums/hooks.enum";
import { DateFormats } from "@enums/shared.enum";
import {
  DateSelectArg,
  EventClickArg,
  EventContentArg,
  EventDropArg,
} from "@fullcalendar/core";
import {
  DateClickArg,
  EventResizeDoneArg,
} from "@fullcalendar/interaction/index.js";
import { useBooking } from "@hooks/useBooking/useBooking";
import { useDrawerLimits } from "@hooks/useDrawerLimits/useDrawerLimits";
import { useKioskAuth } from "@hooks/useKioskAuth/useKioskAuth";
import {
  POSTHOG_ENABLED,
  useTrackSlotSelected,
} from "@hooks/usePosthog/usePosthog";
import { usePricing } from "@hooks/usePricing/usePricing";
import {
  BookingCalendarEvent,
  BookingFetchResponseType,
  ResourceType,
  SingleSlot,
} from "@models/services/booking";
import bookingSlice from "@slices/booking.slice";
import drawerSlice from "@slices/drawer.slice";
import kioskSlice from "@slices/kiosk.slice";
import { RootState } from "@store/index";
import {
  checkQuotaExceeded,
  findNextAvailableTime,
  getDurationConfig,
  mapSlotsByTime,
  validateAdvancedBooking,
  validateConsecutiveSlots,
} from "@utils/booking.utils";
import { buildTimeSlotForResource } from "@utils/bookingTimeSlot.utils";
import { getMinutesDiff } from "@utils/common.utils";

export const useBookingCalendarEvents = (
  slots: BookingFetchResponseType,
  setHasError: (hasError: boolean) => void,
  setIsLoading: (isLoading: boolean) => void
) => {
  const { getSelectedResource } = useBooking();
  const dispatch = useDispatch();
  const [eventClickOrg, setEventClickOrg] = useState<EventClickArg | null>(
    null
  );
  const { maxHeight } = useDrawerLimits();

  const selectedDate = useSelector(
    (State: RootState) => State.booking.selectedDate
  );

  const calendarEvents = useSelector(
    (State: RootState) => State.bookingCalendar.calendarEvents
  );
  const backToBackBookingEnabled = useSelector(
    (State: RootState) => State.booking.backToBackBookingEnabled
  );
  const { isAuthenticated, isKioskMode, requireAuth, requireAuthForSlot } =
    useKioskAuth();

  const pendingSlotSelection = useSelector(
    (state: RootState) => state.kiosk.pendingSlotSelection
  );

  const isSlotsLoading = useSelector(
    (state: RootState) => state.kiosk.isSlotsLoading
  );

  const { fetchQuotaInfo } = usePricing();
  const { trackSlotSelected: trackSlot } = useTrackSlotSelected();

  const updateSelectedSlot = useCallback(
    async (
      startTime: string,
      endTime: string,
      resourceId: string,
      minutesDiff: number
    ) => {
      const selectResource: ResourceType = getSelectedResource(
        slots,
        resourceId
      );

      if (!selectResource || isEmpty(selectResource)) {
        setHasError(true);
        setIsLoading(false);
        return false;
      }

      const timeMaps = buildTimeSlotForResource(
        slots,
        resourceId,
        selectedDate
      );

      dispatch(drawerSlice.actions.setResourceTimeSlot(timeMaps));

      const { allowedDurations, availableSlots, occupiedSlots } =
        selectResource.slotInfo.slots;

      const flatTimeMaps: string[] = timeMaps.flat().map(item => item.time);

      const disabledTimeSlotsArray = mapSlotsByTime(timeMaps.flat());
      let updatedStartTime;
      if (backToBackBookingEnabled) {
        updatedStartTime = startTime;
      } else {
        updatedStartTime = findNextAvailableTime(
          startTime,
          flatTimeMaps,
          disabledTimeSlotsArray,
          allowedDurations,
          occupiedSlots
        );
      }
      if (!updatedStartTime) {
        setHasError(true);
        setIsLoading(false);
        updatedStartTime = "";
      }

      if (!availableSlots.includes(updatedStartTime)) {
        setHasError(true);
        setIsLoading(false);
      }

      const durationResult = getDurationConfig(
        allowedDurations,
        updatedStartTime,
        minutesDiff
      );

      if (!durationResult) {
        setHasError(true);
        setIsLoading(false);
      }

      const areConsecutiveSlotsValid = durationResult
        ? validateConsecutiveSlots(
            updatedStartTime,
            durationResult?.actualDuration || 0,
            availableSlots,
            occupiedSlots
          )
        : false;

      if (!areConsecutiveSlotsValid) {
        setHasError(true);
        setIsLoading(false);
      }

      const quotePayload = {
        bookingDate: selectedDate,
        locationId: slots?.locationId,
        selectedTime: updatedStartTime,
      };

      const quotaData = await fetchQuotaInfo(quotePayload);
      const selectedQuoteInfo = quotaData?.[resourceId] || {};

      const { isExceeded: isQuoteExceeded } = checkQuotaExceeded(
        selectedQuoteInfo?.quotaInfo
      );

      const advancedBookingResult = validateAdvancedBooking(
        selectedDate,
        updatedStartTime,
        selectResource?.policyInfo,
        t
      );

      const isSlotDisabled = disabledTimeSlotsArray[updatedStartTime]?.disabled;

      const isBookable =
        !isQuoteExceeded &&
        advancedBookingResult.isValid &&
        !isEmpty(durationResult?.config) &&
        areConsecutiveSlotsValid &&
        availableSlots.includes(updatedStartTime) &&
        !isSlotDisabled;

      const selectedSlot: SingleSlot = {
        isBookable,
        resourceId,
        slot: {
          allowBringingGuest: selectResource.policyInfo?.allowBringGuest,
          availableForBooking: true,
          disable: !isBookable,
          ends: isBookable ? durationResult?.endTime : "",
          max: durationResult?.config?.max,
          min: durationResult?.config?.min,
          minguestIgnoreAmenities:
            selectResource.policyInfo?.minguestIgnoreAmenities,
          slot: isBookable ? durationResult?.actualDuration : 0,
          starts: updatedStartTime,
          type: slotType.TimeSlots,
        },
        tempEndTime: endTime,
        tempStartTime: startTime,
      };
      dispatch(
        backToBackBookingEnabled
          ? bookingSlice.actions.changeSelectedTime(updatedStartTime)
          : bookingSlice.actions.changeSelectedTime(
              isSlotDisabled ? "" : updatedStartTime
            )
      );
      dispatch(
        drawerSlice.actions.setDrawerBookingData({
          drawerSelectedTime: updatedStartTime || startTime,
          drawerTime: updatedStartTime || startTime,
          resourceId,
          slot: isBookable ? durationResult?.actualDuration : 0,
        })
      );
      dispatch(bookingSlice.actions.changeSelectedSlot(selectedSlot));

      dispatch(drawerSlice.actions.setOpenPercent(maxHeight));

      if (POSTHOG_ENABLED && isBookable) {
        trackSlot(
          resourceId,
          durationResult?.actualDuration || 0,
          updatedStartTime || startTime
        );
      }

      return true;
    },
    [
      backToBackBookingEnabled,
      slots,
      selectedDate,
      fetchQuotaInfo,
      getSelectedResource,
      dispatch,
      setHasError,
      setIsLoading,
      t,
    ]
  );

  const updateSelectedSlotRef = useRef(updateSelectedSlot);
  updateSelectedSlotRef.current = updateSelectedSlot;

  useEffect(() => {
    if (
      !isKioskMode ||
      !isAuthenticated ||
      !pendingSlotSelection ||
      isSlotsLoading
    ) {
      return;
    }

    const { endTime, minutesDiff, resourceId, startTime } =
      pendingSlotSelection;

    dispatch(kioskSlice.actions.setPendingSlotSelection(null));

    setHasError(false);
    setIsLoading(true);
    dispatch(bookingSlice.actions.changeSelectedTime(""));
    dispatch(bookingSlice.actions.changeSelectedSlot({} as SingleSlot));

    updateSelectedSlotRef
      .current(startTime, endTime, resourceId, minutesDiff)
      .then(() => {
        dispatch(drawerSlice.actions.setShowBookingDrawer(true));
        setIsLoading(false);
      });
  }, [
    isAuthenticated,
    isKioskMode,
    pendingSlotSelection,
    isSlotsLoading,
    slots,
  ]);

  const checkIsBlockedByEvent = (info: DateClickArg): boolean => {
    return calendarEvents.some((event: BookingCalendarEvent) => {
      const isDisabled =
        event.type === EventType.Unavailable ||
        event.type === EventType.Blocking ||
        event.classNames?.includes("disabled") ||
        event.classNames?.includes("fg-event-disabled");

      if (!isDisabled) return false;

      if (info.resource?.id && event.resourceId !== info.resource.id) {
        return false;
      }

      const clickTime = info.date.getTime();
      const start = new Date(event.start).getTime();
      const end = new Date(event.end).getTime();

      return clickTime >= start && clickTime < end;
    });
  };

  const handleDateClick = async (info: DateClickArg) => {
    if (checkIsBlockedByEvent(info)) {
      requireAuth();
      return;
    }

    const start = info.date;
    const end = new Date(start);

    end.setMinutes(end.getMinutes() + 15);

    const calendarApi = info.view.calendar;
    calendarApi.select({
      end,
      resourceId: info.resource?.id,
      start,
    });
  };

  const handleTimeSelect = useCallback(
    async (selectInfo: DateSelectArg) => {
      setEventClickOrg(null);
      const startTime = moment(selectInfo.start).format(DateFormats.Time);
      const endTime = moment(selectInfo.end).format(DateFormats.Time);
      const minutesDiff = getMinutesDiff(
        selectInfo.startStr,
        selectInfo.endStr
      );
      const resourceId = selectInfo?.resource?.id ?? "";

      if (
        !requireAuthForSlot({ endTime, minutesDiff, resourceId, startTime })
      ) {
        return;
      }

      const calendarApi = selectInfo.view.calendar;
      calendarApi.unselect();
      setHasError(false);
      setIsLoading(true);

      dispatch(bookingSlice.actions.changeSelectedTime(""));
      dispatch(bookingSlice.actions.changeSelectedSlot({} as SingleSlot));

      await updateSelectedSlot(startTime, endTime, resourceId, minutesDiff);

      dispatch(drawerSlice.actions.setShowBookingDrawer(true));

      setIsLoading(false);
    },
    [
      requireAuthForSlot,
      dispatch,
      updateSelectedSlot,
      setHasError,
      setIsLoading,
    ]
  );

  const handleEventResize = useCallback(
    async (resizeInfo: EventResizeDoneArg) => {
      const { event } = resizeInfo;

      if (event.extendedProps.type !== EventType.TempBooking) {
        return;
      }

      const startTime = moment(event.start).format(DateFormats.Time);
      const endTime = moment(event.end).format(DateFormats.Time);
      const minutesDiff = getMinutesDiff(
        moment(event.start).format(DateFormats.YMDTime),
        moment(event.end).format(DateFormats.YMDTime)
      );
      const resourceId =
        event.getResources()[0]?.id || event.extendedProps.resourceId;

      if (
        !requireAuthForSlot({ endTime, minutesDiff, resourceId, startTime })
      ) {
        return;
      }

      setIsLoading(true);
      await updateSelectedSlot(startTime, endTime, resourceId, minutesDiff);
      setIsLoading(false);
    },
    [requireAuthForSlot, updateSelectedSlot, setIsLoading]
  );

  const handleEventDrop = useCallback(
    async (dropInfo: EventDropArg) => {
      const { event } = dropInfo;

      if (event.extendedProps.type !== EventType.TempBooking) {
        return;
      }

      const startTime = moment(event.start).format(DateFormats.Time);
      const endTime = moment(event.end).format(DateFormats.Time);
      const minutesDiff = getMinutesDiff(
        moment(event.start).format(DateFormats.YMDTime),
        moment(event.end).format(DateFormats.YMDTime)
      );
      const resourceId =
        event.getResources()[0]?.id || event.extendedProps.resourceId;

      if (
        !requireAuthForSlot({ endTime, minutesDiff, resourceId, startTime })
      ) {
        return;
      }

      setIsLoading(true);
      await updateSelectedSlot(startTime, endTime, resourceId, minutesDiff);
      setIsLoading(false);
    },
    [requireAuthForSlot, updateSelectedSlot, setIsLoading]
  );

  const getEventClassName = (arg: EventContentArg) => {
    const type =
      arg.event.extendedProps.subType ??
      (arg.event.extendedProps.type as EventType);

    switch (type) {
      case EventType.Maintenance:
        return ["fg-event-maintenance"];
      case EventType.Blocking:
        return ["fg-event-blocking"];
      case EventType.Exception:
        return ["fg-event-exception"];
      case EventType.AdvanceBooking:
      case EventType.Restriction:
      case EventType.Unavailable:
        return ["fg-event-disabled"];
      case EventType.TempBooking:
        return ["fg-temp-event"];
      case EventType.CampTournament:
        return ["fg-event-camp-tournament"];
      case EventType.Training:
        return ["fg-event-training"];
      case EventType.Booking:
      default:
        return ["fg-event-booking"];
    }
  };

  return useMemo(
    () => ({
      eventClickOrg,
      getEventClassName,
      handleDateClick,
      handleEventDrop,
      handleEventResize,
      handleTimeSelect,
      setEventClickOrg,
    }),
    [
      getEventClassName,
      handleEventResize,
      handleTimeSelect,
      handleEventDrop,
      handleDateClick,
      eventClickOrg,
      setEventClickOrg,
    ]
  );
};
